import dayjs from "dayjs";
import { deepFindFiles, extractFilesFromProp } from "../../reactive/util";
import { FileObject } from "../../../files/file-object";
import { SceneModelData } from "../scene.interface";
import { AssetModelData } from "../../../files/asset.interface";
import { resolveAssetPaths } from "../../reactive/resolve-asset-paths";

export type SceneFilesModel = Pick<
  SceneModelData,
  "layout" | "explanationText" | "assets"
>;

export interface ScenarioFilesModel {
  scenes?: { scene: SceneFilesModel }[];
}
interface FileCollectorOptions {
  skipAssignment?: boolean;
  alwaysReturn?: boolean;
  throwOnIncompleteJob?: boolean;
}

export class FileCollector {
  constructor(private options: FileCollectorOptions = {}) {}
  /**
   * Collect all files that are defined in the tree of all scenes
   * of the given scenario
   * NOTE: This function modifies the passed ScenarioEntity with resource file objects
   */
  public gatherScenarioFiles(scenarios: ScenarioFilesModel[]): FileObject[] {
    const allFiles: FileObject[] = [];

    for (const scenario of scenarios) {
      const { scenes = [] } = scenario;
      for (const { scene } of scenes) {
        allFiles.push(...this.gatherSceneFiles(scene));
      }
    }

    return allFiles;
  }

  public gatherSingleFile(
    fileObject: FileObject,
    assets: AssetModelData[] = []
  ): FileObject | undefined {
    // Collect the file object, this will resolve it to a resource file object
    // if possible, or return undefined if it was already collected before
    return this._collectFileProp(fileObject, assets);
  }

  /**
   * Reset the resolution map so that it no longer checks for duplicates
   */
  public reset() {
    this.resolutionMap = {};
  }

  /**
   * Collect all files in a scene and converts them to a resource-type file object
   * (if possible). If the file could not be converted to a resource-type file object
   * it collects a remote-type file object.
   * @param scene
   */
  public gatherSceneFiles(scene: SceneFilesModel) {
    return [
      ...deepFindFiles(scene.layout, (o) =>
        this._collectFileProp(o, scene.assets)
      ),
      ...extractFilesFromProp(scene.explanationText, (o) =>
        this._collectFileProp(o, scene.assets)
      ),
    ];
  }

  // The resolutionMap keeps track of earlier resolved filed
  private resolutionMap: { [key: string]: FileObject } = {};

  /**
   * Resolve a resource file based on a fileobject, returns undefined if the object
   * was already resolved before
   */
  private _collectFileProp = (
    fileObject: FileObject,
    assets: AssetModelData[] = []
  ): FileObject | undefined => {
    const resolved = this._resolveResourceFile(fileObject, assets);
    const { path } = resolved;

    const existing = !!this.resolutionMap[path];

    // Assign the file in the resolution map to the properties of this file
    if (!this.options.skipAssignment) Object.assign(fileObject, resolved);

    // If file already existed, return undefined so that it gets deduplicated
    // by the file extractor
    if (existing) {
      if (this.options.alwaysReturn) return this.resolutionMap[path];
      return undefined;
    }

    // Otherwise this would be the first time we encountered the file,
    // so return it
    this.resolutionMap[path] = resolved;
    return resolved;
  };

  private _resolveResourcePaths(
    fileObject: FileObject,
    asset?: AssetModelData
  ): FileObject {
    if (this.options.throwOnIncompleteJob && fileObject.operation && asset) {
      // Find out if there are still files processing
      const operation = asset.jobs?.[fileObject.operation.name];

      if (operation?.status === "pending") {
        const diff = 1000 * 60 * 8; // 8 minutes
        if (operation.updated && dayjs.unix(operation.updated).diff() > -diff) {
          throw new Error(
            "Some files in your story are still processing. Please wait for all jobs to complete, and try again."
          );
        }
      }
    }

    const resource = asset
      ? resolveAssetPaths(fileObject, asset)
      : { path: fileObject.path, convertedFilePath: undefined };

    const url = new URL(resource.path);
    const fileKey = `${url.pathname}${url.search}`;

    const metadata = asset?.metadata;

    return {
      path: fileKey,
      originalFilePath: resource.path,
      convertedFilePath: resource.convertedFilePath,
      pathType: "resource",
      metadata: {
        alt: metadata?.alt,
        transcript: metadata?.transcript,
        duration: metadata?.duration,
        resolution: metadata?.resolution,
        // Derived from the asset origin for library assets; remote files
        // (e.g. generated subtitles) carry the flag on the file object itself.
        aiGenerated:
          asset?.origin === "generated" ||
          fileObject.metadata?.aiGenerated ||
          undefined,
      },
      bucketFileKey:
        asset?.origin === "giphy" ||
        asset?.origin === "pexels" ||
        asset?.origin === "unsplash"
          ? undefined
          : fileKey.replace(/^\//, ""),
    };
  }

  /**
   * Resolve a single fileObject (remote file) to  a resource object,
   * does nothing if the fileObject already a resources.
   * @param fileObject
   */
  private _resolveResourceFile(
    fileObject: FileObject,
    assets: AssetModelData[]
  ): FileObject {
    if (fileObject.pathType === "resource") return { ...fileObject };

    const asset = assets.find((a) => a.id === fileObject.assetId);
    return {
      ...fileObject,
      ...this._resolveResourcePaths(fileObject, asset),
      pathType: "resource",
    };
  }
}
